<template>
    <el-tooltip
        effect="dark"
        :popperClass="`elstc-btn_tooltip ${popperClass} ${ alwaysNeedTooltip ? '' : 'hide-in-break-width' }`"
        :openDelay="popperOpenDelay"
        :visibleArrow="false"
        :placement="$attrs.placement || 'top'"
        :disabled="disabledTooltip"
        :popperOptions="popperOptions"
    >
        <template #content>
            <div v-addIframe class="elstc-btn_tooltip-content">
                <slot name="tipContent">
                    {{ tipContent }}
                </slot>
            </div>
        </template>
        <a
            role="button"
            class="elstc-btn"
            :aria-busy="_busy"
            :aria-disabled="_busy || _disabled"
            @click="innerClicked"
        >
            <span class="eb-pre-icon elstc-btn-icon-loading"><PtIcon slot="icon" class="rotating" icon="SolidLoadingCircle" /></span>
            <span class="eb-pre-icon elstc-btn-icon-normal"><slot name="icon" /></span>

            <span v-if="statusText" class="eb-pre-text">{{ statusText }}</span>
            <span v-else class="eb-pre-text"><slot name="text">{{ title }}</slot></span>
            <slot name="default" />
        </a>
    </el-tooltip>
</template>

<script>
import { Icon } from 'patsnap-biz';
import { Tooltip } from 'element-ui';
import { POPPER_OPEN_DELAY } from '../../constants';
import addIframe from '../../directives/addIframe';
import '@patsnap-ui/icon/assets/solid/LoadingCircle.svg';


const POPPER_OPTIONS = {
    gpuAcceleration: true,
};

export default {
    name: 'ElasticButton',
    directives: {
        addIframe,
    },
    components: {
        PtIcon: Icon,
        'el-tooltip': Tooltip,
    },
    props: {
        isLoading: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            required: false,
            default: '',
        },
        popperClass: {
            type: String,
            required: false,
            default: '',
        },
        asyncClick: {
            type: Function,
            default(e) {
                this.$emit('click', e);
                return Promise.resolve(undefined);
            },
        },
        statusText: {
            type: String,
            required: false,
            default: '',
        },
        alwaysNeedTooltip: {
            type: Boolean,
            default: false,
        },
        disabledTooltip: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            tip: '',
            innerLoading: false,
            innerDisabled: false,
        };
    },
    computed: {
        tipContent() {
            return this.tip || this.title;
        },
        _busy() {
            return this.isLoading || this.innerLoading;
        },
        _disabled() {
            return this.disabled || this.innerDisabled;
        },
    },
    created() {
        this.popperOptions = POPPER_OPTIONS;
        this.popperOpenDelay = POPPER_OPEN_DELAY;
    },
    methods: {
        async innerClicked(e) {
            if (this._busy || this._disabled) {
                return;
            }
            this.innerLoading = true;
            try {
                let result = await this.asyncClick(e);
                result = result || {};
                let { disabled, tip, statusText } = result;
                if (typeof disabled === 'boolean') this.innerDisabled = disabled;
                if (typeof tip === 'string') this.tip = tip;
                if (typeof statusText === 'string') this.statusText = statusText;
            } catch (error) {
                console.error(error);
                this.$emit('error', error);
            }
            this.innerLoading = false;
        },
    },
};
</script>

<style lang="scss">
    @keyframes rotating {
        from {
            transform: rotate(0deg);
        }
        to {
            transform: rotate(360deg);
        }
    }
    .rotating {
        animation: rotating 1s linear infinite;
    }
    .elstc-btn{
        user-select: none;
        cursor: pointer;
        color: $neutral-blue-1000;
        &:hover{
            background-color: $neutral-blue-30;
        }
        &:not([aria-disabled="true"]):active {
            background-color: $neutral-blue-10;
        }

        &[aria-disabled="true"] {
            cursor: not-allowed;
            color: $neutral-blue-50;
            .patsnap-biz-icon {
                fill: $neutral-blue-50;
            }
            .eb-pre-text{
                color: $neutral-blue-50;
            }
        }

        &[aria-busy="true"] {
            cursor: progress;
            .elstc-btn-icon-loading{
                display: inline-block;
            }
            .elstc-btn-icon-normal{
                display: none;
            }
        }
    }

    .eb-pre-icon,.eb-pre-text{
        color: $neutral-blue-1000;
        line-height: 1;
        display: inline-block;
        vertical-align: middle;
    }
    .eb-pre-icon {
        fill: $neutral-blue-1000 !important;
    }

    .elstc-btn-icon-loading {
        display: none;
    }
</style>
<style lang="scss">
    $break-width: 1919.5px; // 故意0.5
    $break-width-min: 1080px;
    $ebbutton-padding-vert-big: 0.5em;
    $ebbutton-padding-vert-small: 0.25em;
    .elstc-btn{
        display: inline-block;
        border-radius: 4px;
        padding: 0.25em #{$ebbutton-padding-vert-big + 0.1em} 0.25em #{$ebbutton-padding-vert-big - 0.1em};
        @media (max-width: $break-width-min) {
            padding-right: $ebbutton-padding-vert-small;
            padding-left: $ebbutton-padding-vert-small;
        }

        @media (max-width: $break-width) {
            padding: 0.3em;
        }
    }

    .eb-pre-text{
        @media (max-width: $break-width) {
            display: none;
        }
    }
    .elstc-btn_tooltip.hide-in-break-width{
        @media (min-width: $break-width) {
            display: none;
        }
    }

    .eb-pre-icon.elstc-btn-icon-normal{
        .shine{
            fill: $green !important;
        }
    }

    .elstc-btn_tooltip {
        padding: 0 !important;
        .elstc-btn_tooltip-content {
            position: relative;
            padding: 8px;
            border-radius: 4px;
        }
    }
</style>
